import { ReactNode, useState } from 'react';
import styled from 'styled-components';

import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold, LabelBold } from 'styles/typography';
import CheckIcon from 'svg/CheckIcon';
import ChevronDown from 'svg/ChevronDown';
import WarnIcon from 'svg/WarnIcon';

// The by-property surface's furniture (OLIO-3022): a collapsible section with a status chip, and
// a row per property with a validity mark.
//
// The mark is PRESENCE-based and nothing more. A green check says the property has a value, not
// that the value is right — a botched capture (`Abigail,Smith` in First Name) marks valid, which
// is deliberate: the API carries no per-field validity, confidence or flag anywhere, so any
// stronger claim here would be invented by the browser.

export function FieldMark({ present }: { present: boolean }) {
  return present ? (
    <Valid data-testid='field-mark-valid' aria-label='Present'>
      <CheckIcon width={12} height={12} />
    </Valid>
  ) : (
    <Needed data-testid='field-mark-needed' aria-label='Needs attention'>
      <WarnIcon width={12} height={12} />
    </Needed>
  );
}

export function SectionChip({ complete }: { complete: boolean }) {
  return complete ? (
    <CompleteChip data-testid='section-chip-complete'>Complete</CompleteChip>
  ) : (
    <NeededChip data-testid='section-chip-needed'>Action Needed</NeededChip>
  );
}

interface SectionProps {
  title: string;
  complete: boolean;
  testId: string;
  children: ReactNode;
}

export default function TaskSection({ title, complete, testId, children }: SectionProps) {
  // Open by default, both states. A section that collapsed itself once complete would hide the
  // answer the person just gave, and one that collapsed while incomplete would hide the work.
  const [open, setOpen] = useState(true);

  return (
    <Section data-testid={testId}>
      <Header type='button' onClick={() => setOpen((prev) => !prev)} data-testid={`${testId}-toggle`}>
        <HeaderLeft>
          <Caret $open={open}>
            <ChevronDown color={colors.black75} />
          </Caret>
          <BodySmallBold>{title}</BodySmallBold>
        </HeaderLeft>
        <SectionChip complete={complete} />
      </Header>
      {open && <Rows>{children}</Rows>}
    </Section>
  );
}

export function FieldRow({ label, present, children }: { label: string; present: boolean; children: ReactNode }) {
  return (
    <Row data-testid={`task-field-${label}`}>
      <RowLabel>{label}</RowLabel>
      <RowControl>{children}</RowControl>
      <FieldMark present={present} />
    </Row>
  );
}

export function ReadOnlyValue({ value }: { value: string }) {
  return value ? <BodySmall>{value}</BodySmall> : <Missing>Not provided</Missing>;
}

const Section = styled.div`
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  overflow: hidden;
`;

const Header = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: ${colors.black5};
  border: none;
  cursor: pointer;
  text-align: left;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Caret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  transition: transform 0.15s;
  transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RowLabel = styled(LabelBold)`
  flex: 0 0 120px;
  color: ${colors.black75};
`;

const RowControl = styled.div`
  flex: 1;
  min-width: 0;
`;

const Missing = styled(BodySmall)`
  color: ${colors.black50};
  font-style: italic;
`;

const Chip = styled(LabelBold).attrs({ as: 'span' })`
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
`;

const CompleteChip = styled(Chip)`
  background: ${colors.green10};
  color: ${colors.greenExtraDark};
`;

const NeededChip = styled(Chip)`
  background: ${colors.yellow10};
  color: ${colors.yellowExtraDark};
`;

const Valid = styled.span`
  display: inline-flex;
  color: ${colors.greenExtraDark};
`;

const Needed = styled.span`
  display: inline-flex;
  color: ${colors.yellowExtraDark};
`;
